/**
 * ArtifactsSideBar
 *
 * A slim right-hand sidebar that lists downloadable artifacts for an agent run.
 * Extracted from ArtifactsAndPinnedPromptsSideBar — artifacts only, no pinned prompts.
 */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import { Artifact } from "../AgentDetail/ArtifactsAndPinnedPromptsSideBar";
import { API_BASE, getAuthToken } from "../../api/agentApi";

// ─── helpers ──────────────────────────────────────────────────────────────────

const getArtifactType = (artifact: Artifact): string => {
  if (artifact.type) return artifact.type;
  const f = artifact.filename?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "gif", "svg", "webp"].some((e) => f.endsWith(`.${e}`))) return "image";
  if (f.endsWith(".pdf")) return "pdf";
  if (f.endsWith(".csv")) return "csv";
  if (f.endsWith(".json")) return "json";
  if (f.endsWith(".txt")) return "text";
  return "file";
};

const fetchSecureImage = async (artifact: Artifact): Promise<string | undefined> => {
  if (!artifact.agent_id || !artifact.agent_version || !artifact.run_number || !artifact.filename)
    return artifact.url ?? undefined;
  try {
    const token = getAuthToken();
    const url = `${API_BASE}/agent/${artifact.agent_id}/version/${artifact.agent_version}/runs/${artifact.run_number}/artifact/${encodeURIComponent(artifact.filename)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return undefined;
    return URL.createObjectURL(await res.blob());
  } catch {
    return undefined;
  }
};

const handleDownload = async (artifact: Artifact) => {
  if (!artifact.agent_id || !artifact.agent_version || !artifact.run_number || !artifact.filename) return;
  try {
    const token = getAuthToken();
    const url = `${API_BASE}/agent/${artifact.agent_id}/version/${artifact.agent_version}/runs/${artifact.run_number}/artifact/${encodeURIComponent(artifact.filename)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = artifact.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    console.error("Download failed", e);
  }
};

// ─── ArtifactCard ─────────────────────────────────────────────────────────────

const ArtifactCard: React.FC<{ artifact: Artifact }> = ({ artifact }) => {
  const type = getArtifactType(artifact);
  const isImage = type === "image";
  const [imgUrl, setImgUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isImage) return;
    let mounted = true;
    let blobUrl: string | undefined;
    setLoading(true);
    fetchSecureImage(artifact).then((url) => {
      if (mounted && url) { blobUrl = url; setImgUrl(url); }
      setLoading(false);
    });
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [artifact, isImage]);

  return (
    <Card variant="outlined" sx={{ borderRadius: "10px", overflow: "hidden", transition: "0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: 2 } }}>
      {/* Preview */}
      <Box sx={{ height: 100, bgcolor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isImage ? (
          loading ? <CircularProgress size={22} /> :
          imgUrl ? <img src={imgUrl} alt={artifact.filename} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
          <ImageOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
        ) : type === "pdf" ? (
          <PictureAsPdfOutlinedIcon sx={{ fontSize: 36, color: "error.main" }} />
        ) : (
          <DescriptionIcon sx={{ fontSize: 36, color: "text.disabled" }} />
        )}
      </Box>

      <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Typography variant="caption" color="primary" sx={{ fontWeight: 700, display: "block", mb: 0.4 }}>
          {type}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0, fontSize: "0.74rem" }}>
            {artifact.filename || artifact.name}
          </Typography>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleDownload(artifact); }}
              sx={{ bgcolor: "primary.main", color: "white", "&:hover": { bgcolor: "primary.dark" }, borderRadius: "6px", p: 0.4, flexShrink: 0 }}
            >
              <DownloadIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─── ArtifactsSideBar ─────────────────────────────────────────────────────────

export interface ArtifactsSideBarProps {
  artifacts: Artifact[];
}

const ArtifactsSideBar: React.FC<ArtifactsSideBarProps> = ({ artifacts }) => {
  return (
    <Box
      sx={{
        width: 220,
        minWidth: 220,
        borderLeft: "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fafafa",
      }}
    >
      {/* Header */}
      <Typography
        variant="caption"
        sx={{ px: 2, py: 1.25, fontWeight: 700, color: "text.secondary", display: "block", bgcolor: "#f8fafc", borderBottom: "1px solid rgba(0,0,0,0.06)" }}
      >
        Artifacts
      </Typography>

      {/* List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.1)", borderRadius: "10px" },
        }}
      >
        {artifacts.length === 0 ? (
          <Typography variant="caption" sx={{ color: "text.disabled", textAlign: "center", mt: 3, display: "block" }}>
            No artifacts yet
          </Typography>
        ) : (
          artifacts.map((a) => <ArtifactCard key={a.id} artifact={a} />)
        )}
      </Box>
    </Box>
  );
};

export type { Artifact };
export default ArtifactsSideBar;
